import React, { useState, useEffect } from 'react';
import ApiService from '../utils/ApiService';
import './../styles/ChatbotManager.css';

interface CLAIBot {
  id: string;
  bot_name: string;
  // Add other CLAIbot properties as needed
}

interface CLAIBotListResponse {
  chatbots: CLAIBot[]; // Keep 'chatbots' if this matches the API response key
  total: number;
}

const ChatbotManager: React.FC = () => {
  const [claiBots, setCLAIBots] = useState<CLAIBot[]>([]);
  const [selectedCLAIBot, setSelectedCLAIBot] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
  const [newBotName, setNewBotName] = useState<string>('');

  useEffect(() => {
    const fetchCLAIBots = async () => {
      try {
        setIsLoading(true);
        // Assuming ApiService.listChatbots() returns a structure with a 'chatbots' field
        const response: CLAIBotListResponse = await ApiService.listChatbots();
        setCLAIBots(response.chatbots); // Use response.chatbots if that's the key from API
        if (response.chatbots.length > 0) {
          setSelectedCLAIBot(response.chatbots[0].id);
        }
        setIsLoading(false);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch CLAIbots.');
        setIsLoading(false);
      }
    };

    fetchCLAIBots();
  }, []);

  const handleCreateCLAIBot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBotName.trim()) {
      setError('Bot name cannot be empty.');
      return;
    }
    setError(null);
    try {
      const newCLAIBot = await ApiService.createChatbot({
        bot_name: newBotName,
        // Add other necessary fields for CLAIbot creation if your schema requires them
      });
      setCLAIBots([...claiBots, newCLAIBot]);
      setSelectedCLAIBot(newCLAIBot.id);
      setShowCreateForm(false);
      setNewBotName('');
    } catch (err: any) {
      setError(err.message || 'Failed to create CLAIbot.');
    }
  };

  if (isLoading) {
    return <div className="chatbot-manager-loading">Loading CLAIbots...</div>;
  }

  if (error) {
    return <div className="chatbot-manager-error">Error: {error}</div>;
  }

  return (
    <div className="chatbot-manager-container">
      {claiBots.length === 0 && !showCreateForm && (
        <div className="chatbot-manager-no-bots">
          <p>No CLAIbots found.</p>
          <button onClick={() => setShowCreateForm(true)} className="chatbot-manager-button">
            Create New CLAIbot
          </button>
        </div>
      )}

      {showCreateForm && (
        <form onSubmit={handleCreateCLAIBot} className="chatbot-manager-form">
          <h3>Create New CLAIbot</h3>
          <input
            type="text"
            value={newBotName}
            onChange={(e) => setNewBotName(e.target.value)}
            placeholder="Enter CLAIbot name"
            className="chatbot-manager-input"
          />
          <button type="submit" className="chatbot-manager-button">Create</button>
          <button type="button" onClick={() => setShowCreateForm(false)} className="chatbot-manager-button secondary">
            Cancel
          </button>
        </form>
      )}

      {claiBots.length > 0 && !showCreateForm && (
        <div className="chatbot-manager-selection">
          <h3>Select a CLAIbot:</h3>
          <select
            value={selectedCLAIBot || ''}
            onChange={(e) => setSelectedCLAIBot(e.target.value)}
            className="chatbot-manager-select"
          >
            {claiBots.map((bot) => (
              <option key={bot.id} value={bot.id}>
                {bot.bot_name}
              </option>
            ))}
          </select>
          <button onClick={() => setShowCreateForm(true)} className="chatbot-manager-button create-another">
            Create Another CLAIbot
          </button>
          {/* Display more CLAIbot details or actions here if needed */}
        </div>
      )}
    </div>
  );
};

export default ChatbotManager;
